import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chatApi } from '../services/api';
import { SendChatMessagePayload } from '../types/api';
import { TODOS_KEY } from './useTodos';
import { CATEGORIES_KEY } from './useCategories';
import { TIME_LOGS_KEY } from './useTimeLogs';

export const CHAT_THREADS_KEY = ['chat', 'threads'] as const;
export const chatThreadKey = (id: number) => ['chat', 'thread', id] as const;
export const CHAT_USAGE_KEY = ['chat', 'usage'] as const;

export const useChatThreads = () =>
  useQuery({ queryKey: CHAT_THREADS_KEY, queryFn: chatApi.listThreads });

export const useChatUsage = () => useQuery({ queryKey: CHAT_USAGE_KEY, queryFn: chatApi.getUsage });

export const useChatThread = (threadId: number | null) =>
  useQuery({
    queryKey: chatThreadKey(threadId ?? -1),
    queryFn: () => chatApi.getThread(threadId as number),
    enabled: threadId != null,
  });

export const useSendChatMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SendChatMessagePayload) => chatApi.sendMessage(payload),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: chatThreadKey(result.thread_id) });
      queryClient.invalidateQueries({ queryKey: CHAT_THREADS_KEY });
      // The agent may have created/changed tasks, categories or time logs.
      if (result.tool_calls?.length) {
        queryClient.invalidateQueries({ queryKey: TODOS_KEY });
        queryClient.invalidateQueries({ queryKey: CATEGORIES_KEY });
        queryClient.invalidateQueries({ queryKey: TIME_LOGS_KEY });
      }
    },
    // Rejected messages can cost credits too (e.g. blocked by the scope guard).
    // Not returned: a returned promise would make the mutation wait for this
    // refetch before the page's own onSuccess/onError run.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CHAT_USAGE_KEY });
    },
  });
};

export const useDeleteChatThread = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (threadId: number) => chatApi.deleteThread(threadId),
    onSuccess: (_, threadId) => {
      queryClient.removeQueries({ queryKey: chatThreadKey(threadId) });
      queryClient.invalidateQueries({ queryKey: CHAT_THREADS_KEY });
    },
  });
};
